import { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { jwtDecode } from "jwt-decode"

// @mui material components
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"

// Material Dashboard 2 React example components
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// SweetAlert
import { showToast } from "utils/swalToast"

// Services
import {
  fetchDependencias,
  fetchTiposAsistente,
  fetchConfiguracionById,
  createConfiguracion,
  updateConfiguracion,
} from "./service"

function ConfiguracionAdd() {
  const { id } = useParams()
  const navigate = useNavigate()
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const isEditMode = Boolean(id)

  // State for form data
  const [formData, setFormData] = useState({
    id: null,
    dependenciaId: "",
    tipoAsistenteId: "",
    diaSemana: "",
    horaEntradaEsperada: "",
    horaSalidaEsperada: "",
    minutosToleranciaEntrada: 0,
    minutosToleranciaSalida: 0,
    activo: true,
  })

  // State for dropdowns
  const [dependencias, setDependencias] = useState([])
  const [tiposAsistente, setTiposAsistente] = useState([])

  // State for loading
  const [loading, setLoading] = useState(false)

  const diasSemana = [
    { value: "LUNES", label: "Lunes" },
    { value: "MARTES", label: "Martes" },
    { value: "MIERCOLES", label: "Miércoles" },
    { value: "JUEVES", label: "Jueves" },
    { value: "VIERNES", label: "Viernes" },
    { value: "SABADO", label: "Sábado" },
    { value: "DOMINGO", label: "Domingo" },
  ]

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadDependencias()
    loadTiposAsistente()
  }, [])

  // Load data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadConfiguracionData()
    }
  }, [isEditMode, id])

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias()
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading organizaciones:", error)
    }
  }

  const loadTiposAsistente = async () => {
    try {
      const data = await fetchTiposAsistente()
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
    }
  }

  const loadConfiguracionData = async () => {
    setLoading(true)
    try {
      const data = await fetchConfiguracionById(id)
      setFormData({
        id: data.id,
        dependenciaId: data.dependenciaId || "",
        tipoAsistenteId: data.tipoAsistenteId || "",
        diaSemana: data.diaSemana || "",
        horaEntradaEsperada: data.horaEntradaEsperada || "",
        horaSalidaEsperada: data.horaSalidaEsperada || "",
        minutosToleranciaEntrada: data.minutosToleranciaEntrada || 0,
        minutosToleranciaSalida: data.minutosToleranciaSalida || 0,
        activo: data.activo !== undefined ? data.activo : true,
      })
    } catch (error) {
      console.error("Error loading configuración:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validations
    if (!formData.dependenciaId) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor seleccione una dependencia" })
      return
    }

    if (!formData.diaSemana) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor seleccione un día de la semana" })
      return
    }

    if (!formData.horaEntradaEsperada) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor ingrese la hora de entrada esperada" })
      return
    }

    if (!formData.horaSalidaEsperada) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor ingrese la hora de salida esperada" })
      return
    }

    setLoading(true)
    try {
      const dataToSend = {
        id: formData.id,
        dependenciaId: formData.dependenciaId,
        tipoAsistenteId: formData.tipoAsistenteId || null,
        diaSemana: formData.diaSemana,
        horaEntradaEsperada: formData.horaEntradaEsperada,
        horaSalidaEsperada: formData.horaSalidaEsperada,
        minutosToleranciaEntrada: parseInt(formData.minutosToleranciaEntrada) || 0,
        minutosToleranciaSalida: parseInt(formData.minutosToleranciaSalida) || 0,
        activo: formData.activo,
      }

      if (isEditMode) {
        await updateConfiguracion(id, dataToSend)
      } else {
        await createConfiguracion(dataToSend)
      }

      navigate("/asistencia/configuraciones")
    } catch (error) {
      console.error("Error saving configuración:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">
                  {isEditMode ? "Editar configuración de horario" : "Agregar configuración de horario"}
                </MDTypography>
              </MDBox>
              <MDBox pt={2} px={2} component="form" role="form" onSubmit={handleSubmit}>
                {/* Dependencia */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Dependencia</InputLabel>
                    <Select
                      label="Dependencia"
                      name="dependenciaId"
                      value={formData.dependenciaId}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una dependencia</MenuItem>
                      {dependencias.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Tipo de Asistente */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Asistente (Opcional)</InputLabel>
                    <Select
                      label="Tipo de Asistente (Opcional)"
                      name="tipoAsistenteId"
                      value={formData.tipoAsistenteId}
                      onChange={handleInputChange}
                      disabled={loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Ninguno (Aplica a todos)</MenuItem>
                      {tiposAsistente.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Día de Semana */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Día de la Semana</InputLabel>
                    <Select
                      label="Día de la Semana"
                      name="diaSemana"
                      value={formData.diaSemana}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione un día</MenuItem>
                      {diasSemana.map((dia) => (
                        <MenuItem key={dia.value} value={dia.value}>
                          {dia.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Horas de Entrada y Salida */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="time"
                        name="horaEntradaEsperada"
                        label="Hora de Entrada Esperada"
                        fullWidth
                        required
                        value={formData.horaEntradaEsperada}
                        onChange={handleInputChange}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="time"
                        name="horaSalidaEsperada"
                        label="Hora de Salida Esperada"
                        fullWidth
                        required
                        value={formData.horaSalidaEsperada}
                        onChange={handleInputChange}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                      />
                    </MDBox>
                  </Grid>
                </Grid>

                {/* Tolerancias */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        name="minutosToleranciaEntrada"
                        label="Minutos de Tolerancia Entrada"
                        fullWidth
                        value={formData.minutosToleranciaEntrada}
                        onChange={handleInputChange}
                        disabled={loading}
                        inputProps={{ min: 0 }}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        name="minutosToleranciaSalida"
                        label="Minutos de Tolerancia Salida"
                        fullWidth
                        value={formData.minutosToleranciaSalida}
                        onChange={handleInputChange}
                        disabled={loading}
                        inputProps={{ min: 0 }}
                      />
                    </MDBox>
                  </Grid>
                </Grid>

                {/* Activo */}
                <MDBox mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="activo"
                        checked={formData.activo}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                    }
                    label="Activo"
                  />
                </MDBox>

                {/* Botones */}
                <MDBox mb={2} sx={{ textAlign: "center" }}>
                  <Link to="/asistencia/configuraciones">
                    <MDButton variant="outlined" color="info" sx={{ mr: 1, width: 90 }} disabled={loading}>
                      Cancelar
                    </MDButton>
                  </Link>
                  <MDButton type="submit" variant="gradient" color="info" sx={{ ml: 1, width: 90 }} disabled={loading}>
                    {loading ? "..." : isEditMode ? "Actualizar" : "Agregar"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default ConfiguracionAdd
